import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

export const TermsPage = () => {
  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="terms-page">
      {/* Header */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase mb-4">
              Terms of <span className="text-amber-500">Service</span>
            </h1>
            <p className="text-slate-300">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-none">
            <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 mb-8">
              <p className="text-slate-300 leading-relaxed">
                Welcome to Pro Painting. By accessing our website or using our services, you agree 
                to be bound by these Terms of Service. Please read them carefully before using our 
                website or engaging our services.
              </p>
            </div>

            <div className="space-y-8">
              {/* Services */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  1. Services Provided
                </h2>
                <p className="text-slate-300 mb-4">
                  Pro Painting provides residential and commercial painting services, including but not limited to:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Interior and exterior painting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Drywall and sheetrock repair</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Pressure washing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Trim, door, and cabinet painting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Surface preparation (caulking, patching, sanding)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Project consultations and estimates</span>
                  </li>
                </ul>
                <p className="text-slate-300 mt-4">
                  Our website also provides consultation scheduling, estimate requests, and coordination 
                  messaging services.
                </p>
              </div>

              {/* Estimates and Proposals */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  2. Estimates and Proposals
                </h2>
                <p className="text-slate-300 mb-4">
                  All estimates provided are based on information available at the time of assessment. 
                  Final pricing may vary based on:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Actual conditions discovered during the project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Changes requested by the customer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Additional repairs or preparation work needed</span>
                  </li>
                </ul>
                <p className="text-slate-300 mt-4">
                  Written proposals will detail the scope of work, materials, timeline, and pricing. 
                  Any changes to the original scope may result in adjusted pricing.
                </p>
              </div>

              {/* No Guarantee */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  3. No Guarantee
                </h2>
                <p className="text-slate-300">
                  While we strive to provide excellent service and quality workmanship, we cannot 
                  guarantee specific results or outcomes. Factors such as surface conditions, 
                  weather, substrate quality, and other variables may affect the final result. 
                  We will work diligently to address any concerns and ensure customer satisfaction 
                  within reasonable expectations.
                </p>
              </div>

              {/* Limitation of Liability */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  4. Limitation of Liability
                </h2>
                <p className="text-slate-300 mb-4">
                  To the maximum extent permitted by law:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Pro Painting shall not be liable for any indirect, incidental, special, 
                    consequential, or punitive damages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Our total liability shall not exceed the amount paid for services rendered</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>We are not responsible for pre-existing conditions or defects not caused by our work</span>
                  </li>
                </ul>
              </div>

              {/* Payment Terms */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  5. Payment Terms
                </h2>
                <p className="text-slate-300 mb-4">
                  Payment terms will be outlined in your written proposal and may include:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Deposit required before work begins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Progress payments for larger projects</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Final payment due upon satisfactory completion</span>
                  </li>
                </ul>
                <p className="text-slate-300 mt-4">
                  We accept various payment methods including credit/debit cards processed securely 
                  through Stripe. All payments are subject to verification.
                </p>
              </div>

              {/* SMS Terms Summary */}
              <div className="bg-slate-900 border border-cyan-500/30 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  6. SMS/Text Messaging Terms
                </h2>
                <p className="text-slate-300 mb-4">
                  If you opt in to receive SMS communications from Pro Painting:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Message frequency may vary</strong> based on your project and interactions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Message and data rates may apply</strong> depending on your carrier plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Reply STOP to opt out</strong> at any time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Reply HELP for assistance</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Consent is not a condition of purchase</strong></span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-800">
                  <h3 className="font-bold text-white mb-3">SMS Liability Disclaimer</h3>
                  <p className="text-slate-300 text-sm">
                    Pro Painting and its affiliates are not liable for any delays or failures in SMS message delivery. 
                    Mobile carriers are not responsible for delayed or undelivered messages. Message delivery is subject 
                    to effective transmission from your network operator. Pro Painting reserves the right to modify or 
                    discontinue SMS services at any time without notice.
                  </p>
                </div>
                <p className="text-slate-300 mt-4">
                  For complete SMS terms, please see our{' '}
                  <Link to="/sms-terms" className="text-amber-500 hover:text-amber-400">
                    SMS Terms / Mobile Terms of Service
                  </Link>.
                </p>
              </div>

              {/* Website Use */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  7. Website Use
                </h2>
                <p className="text-slate-300 mb-4">
                  By using our website, you agree to:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Provide accurate and complete information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Use the website for lawful purposes only</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Not attempt to interfere with website functionality</span>
                  </li>
                </ul>
              </div>

              {/* Modifications */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  8. Modifications
                </h2>
                <p className="text-slate-300">
                  We reserve the right to modify these Terms of Service at any time. Changes will 
                  be effective immediately upon posting to our website. Your continued use of our 
                  services after any changes constitutes acceptance of the updated terms.
                </p>
              </div>

              {/* Governing Law */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  9. Governing Law
                </h2>
                <p className="text-slate-300">
                  These Terms of Service shall be governed by and construed in accordance with the 
                  laws of the State of Texas, without regard to its conflict of law provisions.
                </p>
              </div>

              {/* Contact Information */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Contact Information
                </h2>
                <p className="text-slate-300 mb-4">
                  For questions about these Terms of Service, please contact us:
                </p>
                <div className="space-y-3">
                  <p className="text-white font-bold">Pro Painting</p>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Mail className="w-5 h-5 text-amber-500" />
                    <a href="mailto:matthew@propainting.cloud" className="hover:text-amber-500 transition-colors">
                      matthew@propainting.cloud
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Phone className="w-5 h-5 text-amber-500" />
                    <a href="tel:+18303270136" className="hover:text-amber-500 transition-colors">
                      (830) 327-0136
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <MapPin className="w-5 h-5 text-amber-500" />
                    <span>493 N Market St, New Braunfels, TX 78130</span>
                  </div>
                </div>
              </div>

              {/* Related Links */}
              <div className="flex flex-wrap gap-4 pt-6 border-t border-slate-800">
                <Link 
                  to="/privacy-225682" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link 
                  to="/sms-terms" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  SMS Terms
                </Link>
                <Link 
                  to="/contact" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsPage;
